import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
} from "react-flow-renderer";
import dagre from "dagre";
import NodeWrapper from "./NodeWrapper";

// Define nodeTypes outside of the component
const nodeTypes = {
  custom: NodeWrapper, // Custom node for TankIcon
};

const applyDagreLayout = (nodes, edges, direction = 'TB') => {
  const nodeWidth = 180;
  const nodeHeight = 100;

  const g = new dagre.graphlib.Graph();
  const isHorizontal = direction === 'LR';

  g.setGraph({
    rankdir: direction,
    nodesep: 100,
    ranksep: 150,
  });

  g.setDefaultEdgeLabel(() => ({}));

  // 1. Group nodes by operation_id
  const operationMap = new Map();
  nodes.forEach((node) => {
    const opId = node.data?.operation_id;
    if (!opId) return;
    if (!operationMap.has(opId)) operationMap.set(opId, []);
    operationMap.get(opId).push(node);
  });

  // 2. Sort operation groups by completion_date
  const sortedOperations = Array.from(operationMap.entries()).sort(
    ([, aNodes], [, bNodes]) => {
      const aDate = new Date(aNodes[0]?.data?.completion_date || 0);
      const bDate = new Date(bNodes[0]?.data?.completion_date || 0);
      return aDate - bDate;
    }
  );

  // 3. Create dummy operation-level nodes to define order (timeline)
  const operationAnchors = [];
  sortedOperations.forEach(([opId, opNodes], index) => {
    const anchorId = `anchor_${opId}`;
    g.setNode(anchorId, { width: 1, height: 1 }); // invisible anchor node
    operationAnchors.push(anchorId);
    
    // Connect real nodes to their anchor to group them
    opNodes.forEach((node) => {
      // console.log('nn', node)
      g.setNode(node.id, { width: nodeWidth, height: nodeHeight });
      g.setEdge(anchorId, node.id); // anchor "owns" its group
    });
  });

  // 4. Add timeline ordering between operation anchors
  for (let i = 1; i < operationAnchors.length; i++) {
    g.setEdge(operationAnchors[i - 1], operationAnchors[i]);
  }

  // 5. Add real edges (if you want them to influence the layout)
  edges.forEach((edge) => {
    if (g.hasNode(edge.source) && g.hasNode(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(g);

  // 6. Apply calculated positions to nodes
  const newNodes = nodes.map((node) => {
    const dagreNode = g.node(node.id);
    if (!dagreNode) return node;

    return {
      ...node,
      targetPosition: isHorizontal ? 'left' : 'top',
      sourcePosition: isHorizontal ? 'right' : 'bottom',
      position: {
        x: dagreNode.x - nodeWidth / 2,
        y: dagreNode.y - nodeHeight / 2,
      },
    };
  });

  return newNodes;
};

const getColor = (color = "RED") => {
  switch (color) {
    case "RED":
      return "#64242e";
    case "ROSE":
      return "#c74f59";
    case "WHITE":
      return "#f9d573";
    default:
      return "#64242e";
  }
};

const FlowChart = ({ operations, toggleShowOperationData, flowType, id = null }) => {
  // const { fitView } = useReactFlow();

  const getMatchingTransfer = (data, currentDate, currentSrcTankId, currentDestTankId = null) => {
    // return always the last match
    let latestMatch = null;
    let latestDate = null;
    
    for (const prevOp of data) {
      if (!prevOp.completion_date || !prevOp.transfers) continue;
      
      const prevDate = new Date(prevOp.completion_date);

      if (prevDate < currentDate) {
        // const match = flowType === 'lot' ? prevOp?.transfers.find(t => t?.destination_tank_id == currentSrcTankId)
        //   : prevOp?.transfers.find(t => t?.source_tank_id == currentDestTankId);
        const match = prevOp?.transfers.find(t => t?.destination_tank_id == currentSrcTankId);

        if (match && (!latestDate || prevDate > latestDate)) {
          latestMatch = { ...match, operation_id: prevOp?.operation_id };
          latestDate = prevDate;
        }
      }
    }
  
    return latestMatch;
  };  
  
  const getIsHideParent = (data, operation, flowType, currentCutSrcTankId = null) => {
    const firstTransfer = operation?.transfers?.[0];

    const currentDate = new Date(operation?.completion_date);
    const currentSrcTankId = currentCutSrcTankId ? currentCutSrcTankId : firstTransfer?.source_tank_id;
    const currentDestTankId = firstTransfer?.destination_tank_id;

    return !!getMatchingTransfer(data, currentDate, currentSrcTankId, currentDestTankId, flowType);
  };

  const addEdge = ({
    edges,
    id,
    source,
    target,
    label = null,
    color,
    sourceHandle,
    targetHandle,
    isFocused = false
  }) => {
    const lineColor = isFocused && color ? getColor(color) : '#060b0f80'; // variables.$Black50
    
    edges.push({
      id,
      source,
      target,
      label,
      sourceHandle,
      targetHandle,
      animated: true,
      arrowHeadType: "arrowclosed",
      type: "smoothstep",
      style: { stroke: lineColor, strokeWidth: isFocused ? 3 : 2 },
      markerEnd: {
        type: MarkerType.Arrow,
        width: 15,
        height: 15,
        color: lineColor,
      },
    });
  }
  
  const addNode = (nodes, {
    id,
    type = "custom",
    x,
    y,
    data = {}
  }) => {
    nodes.push({
      id,
      type,
      position: { x, y },
      data,
    });
  };
  
  const initialNodesAndEdges = (data) => {
    const nodes = [];
    const edges = [];
  
    const tankStateMap = new Map(); // Tracks the latest node for each tank
    
    data?.forEach((operation, i) => {
    
      // Handle DESTEMMING, NEW_LOT, DECANT and BOTTLING operations
      if (["DESTEMMING", "NEW_LOT", "DECANT", "RACKING", "BOTTLING", "CLEANING", "CLOSING"].includes(operation.operation_type)) {
        
        // Add a parent node for the operation
        const parentNodeId = `parent_${operation.operation_id}`;

        const isHideParent = getIsHideParent(data?.slice(0, i), operation, flowType);
        const firstTransfer = operation?.transfers?.[0];
        
        addNode(nodes, { // 1: Parent node
          id: parentNodeId,
          data: {
            operation_id: operation.operation_id,
            operation_type: operation.operation_type,
            completion_date: operation.completion_date,
            ...(["DESTEMMING", "NEW_LOT"].includes(operation.operation_type)
              ? { lot: { ...firstTransfer } }
              : {
                  tank: {
                    hide: isHideParent,
                    ...firstTransfer,
                    name: firstTransfer?.source_tank_name,
                    batch_name: firstTransfer?.source_batch_name,
                    batch_type: firstTransfer?.source_batch_type,
                    state: firstTransfer?.state,
                    quantity: firstTransfer?.source_batch_quantity,
                    is_focused: flowType === 'tank' && firstTransfer?.source_tank_id === Number(id),
                    type: firstTransfer?.source_tank_type,
                  }
                }),
          },
        });
  
        const currentDate = new Date(operation?.completion_date);
      
        operation?.transfers?.forEach((transfer, i) => {

          // if (flowType === "lot" && i > 0) {
            const currentSrcTankId = transfer?.source_tank_id;

            const prevTransfer = getMatchingTransfer(data?.slice(0, i), currentDate, currentSrcTankId);
            const prevOperationId = prevTransfer?.operation_id;

            if (
              prevTransfer?.destination_tank_id &&
              transfer?.source_tank_id &&
              prevTransfer.destination_tank_id === transfer.source_tank_id
            ) {
              const sourceNodeId = `${prevOperationId}_${prevTransfer.destination_tank_id}`;
              const targetNodeId = parentNodeId;
              
              addEdge({
                edges,
                id: `e_${sourceNodeId}-${targetNodeId}`,
                source: sourceNodeId,
                target: targetNodeId,
                color: prevTransfer?.color,
                isFocused: flowType === 'tank' && transfer?.source_tank_id === Number(id),
              });           
            }
          // }
          
          const isBottling = operation?.operation_type === 'BOTTLING';
          const isClosing = operation?.operation_type === 'CLOSING';

          if (isClosing) console.log('CLOSING operation - operation:', transfer?.quantity, 'index', i);
          // Create a new node for the tank's current state
          const currentNodeId = `${operation.operation_id}_${isClosing ? transfer?.transfer_id : isBottling ? transfer?.bottling_id : transfer.destination_tank_id}`;

          addNode(nodes, { // 2: Destination tank state
            id: currentNodeId,
            data: {
              operation_id: operation?.operation_id,
              operation_type: operation.operation_type,
              tank: {
                is_dest: true,
                ...(isBottling && { cap_lot: transfer?.cap_lot, bottle_lot: transfer?.bottle_lot, bottle_type: transfer?.bottle_type }),
                // hide: (["DESTEMMING", "NEW_LOT"].includes(operation.operation_type)),
                name: isBottling ? transfer.destination_batch_name : transfer.destination_tank_name,
                batch_name: transfer.batch_name,
                batch_type: transfer?.batch_type,
                state: transfer?.state,
                quantity: transfer.quantity - (transfer.drop || 0),
                capacity: transfer.capacity,
                color: transfer.color,
                type: transfer?.tank_type,
                is_focused: isClosing ? true : flowType === 'tank' && ((isBottling && transfer?.source_tank_id === Number(id)) || (!isBottling && (transfer?.destination_tank_id === Number(id)))),
                ...(isClosing &&
                {
                  close_dest: transfer?.destination_buyer_name,
                  close_reason: transfer?.destination_buyer_reason,
                }),
              },
            }
          });
          // Connect parent operation node to the current tank state node
          addEdge({
            edges,
            id: `e_${parentNodeId}-${currentNodeId}`,
            source: parentNodeId,
            target: currentNodeId,
            label: `${transfer.arrow_quantity}L`,
            color: transfer?.color,
            isFocused: flowType === 'tank' && ((isBottling && transfer.source_tank_id === Number(id)) || (!isBottling && transfer.destination_tank_id === Number(id))),
          });
  
          // Check if this tank has a previous state
          const previousTankNodeId = tankStateMap.get(transfer.destination_tank_id);
          // const previousSrcTankNodeId = tankStateMap.get(transfer.source_tank_id);
          if (!isBottling && !isClosing && previousTankNodeId && previousTankNodeId !== currentNodeId) {
            // if (operation?.operation_id === 1252) console.log('previousTankNodeId', previousTankNodeId, 'parentNodeId', parentNodeId, 'currentNodeId', currentNodeId);
            // Connect the previous state of the tank to the current state
            addEdge({
              edges,
              id: `e_${previousTankNodeId}-${currentNodeId}`,
              source: previousTankNodeId,
              target: currentNodeId,
              color: transfer?.color,
              isFocused: flowType === 'tank' && (transfer.source_tank_id === Number(id) || transfer.destination_tank_id === Number(id)),
            });
          }
          // Update the latest state of the tank in the map
          tankStateMap.set(transfer.destination_tank_id, currentNodeId);
  
        });
      }
      // Handle CUT operation type
      else if (operation.operation_type === "CUT") {
        // yOffset += 200;

        // No parent node for CUT, only source and destination nodes
        // const spacing = 300; // Horizontal spacing between source nodes
        // let xPosition = xOffsetStart;
        
        // Create a node for each source tank
        operation?.transfers?.forEach((transfer) => {
          const sourceNodeId = `${operation.operation_id}_${transfer.source_tank_id}`;

          const srcTankNodeId = tankStateMap.get(transfer.source_tank_id);

          const [prevOpId, prevDestTankId] = srcTankNodeId?.split('_')?.map(Number) || [];
          const prevOp = data?.find(op => op.operation_id === prevOpId);
          
          // if the current src is a dest of a previous op and the quantity is unchanged, no need to add the same node again
          const isHideParent = getIsHideParent(data?.slice(0, i), operation, flowType, transfer.source_tank_id);
          // console.log('isHideParent', isHideParent, 'operation', operation, 'currentSrcTankId', transfer.source_tank_id, 'prevDest', prevDestTankId)
          addNode(nodes, { // 3: Source tank
            id: sourceNodeId,
            // x: xPosition,
            // y: yOffset + (isHideParent ? 100 : 0),
            data: {
              operation_id: operation.operation_id,
              operation_type: operation.operation_type,
              completion_date: operation.completion_date,
              tank: {
                hide: isHideParent,
                name: transfer.source_tank_name,
                batch_name: transfer.batch_name,
                batch_type: transfer?.batch_type,
                state: transfer?.state,
                quantity: transfer.src_quantity,
                capacity: transfer.capacity,
                color: transfer.color,
                type: transfer?.source_tank_type,
              }
            }
          });
          
          // xPosition += spacing;
        });
        const firstTransfer = operation?.transfers?.[0];
        // Create a destination node for the CUT operation (only one destination tank)
        const destinationNodeId = `${operation.operation_id}_${firstTransfer?.destination_tank_id}`;

        addNode(nodes, { // 4: CUT destination node
          id: destinationNodeId,
          // x: xOffsetStart + spacing * (operation?.transfers?.length || 1),
          // y: yOffset + 400,
          data: {
            operation_id: operation.operation_id,
            operation_type: operation.operation_type,
            completion_date: operation.completion_date,
            tank: {
              is_dest: true,
              name: firstTransfer?.destination_tank_name,
              batch_name: firstTransfer?.batch_name,
              batch_type: firstTransfer?.batch_type,
              state: firstTransfer?.state,
              quantity: firstTransfer?.quantity,
              capacity: firstTransfer?.capacity,
              color: firstTransfer?.color,
              type: firstTransfer?.tank_type,
            }
          }
        });

        tankStateMap.set(firstTransfer?.destination_tank_id, destinationNodeId);
      
        // Connect all source nodes to the destination node
        operation?.transfers?.forEach((transfer) => {
          const sourceNodeId = `${operation.operation_id}_${transfer.source_tank_id}`;
          addEdge({
            edges,
            id: `e_${sourceNodeId}-${destinationNodeId}`,
            source: sourceNodeId,
            target: destinationNodeId,
            label: `${transfer.quantity}L`,
            color: transfer?.color,
            isFocused: flowType === 'tank' && transfer.source_tank_id === Number(id),
          });      

          const srcTankNodeId = tankStateMap.get(transfer.source_tank_id);
          const destNodeId = `${operation.operation_id}_${transfer.source_tank_id}`;

          const actualSrcTankQuantity = Number(transfer?.src_quantity) || 0;
          const [prevOpId, prevDestTankId] = srcTankNodeId?.split('_')?.map(Number) || [];
          const prevOp = data?.find(op => op.operation_id === prevOpId);
          const prevDestTankQuantity = Number(prevOp?.transfers?.find(t => t?.destination_tank_id === prevDestTankId)?.quantity) || 0;
          
          if (transfer.source_tank_id === prevDestTankId && prevDestTankQuantity === actualSrcTankQuantity) {   // if this source is dest of a previous operation

            prevOp?.transfers?.filter(t => t?.destination_tank_id === transfer.source_tank_id)?.forEach((tt) => {
              addEdge({
                edges,
                id: `e_${srcTankNodeId}-${sourceNodeId}`,
                source: srcTankNodeId,
                target: sourceNodeId,
                label: `${tt.src_arrow_quantity || ''}L`,
                color: tt?.color,
                sourceHandle: 'bottom',
                targetHandle: 'top',
              });              
            });
          } else if (prevDestTankQuantity !== actualSrcTankQuantity) {

            addEdge({
              edges,
              id: `e_${sourceNodeId}-${srcTankNodeId}`,
              source: srcTankNodeId,
              target: destNodeId,
              label: `${transfer.quantity || ''}L`,
              color: transfer?.color,
              sourceHandle: 'bottom',
              targetHandle: 'top',
            });            
          }
        });

        // NEW: Connect this destination node to the previous destination node for the same tank
        const destTankId = firstTransfer?.destination_tank_id;
        const previousDestinationNodeId = tankStateMap.get(destTankId);
        
        if (previousDestinationNodeId && previousDestinationNodeId !== destinationNodeId && flowType === 'tank' && destTankId !== Number(id)) {
          addEdge({
            edges,
            id: `e_${previousDestinationNodeId}-${destinationNodeId}`,
            source: previousDestinationNodeId,
            target: destinationNodeId,
            color: firstTransfer?.color
          });          
        }
        // Update the map with the current destination node for that tank
        tankStateMap.set(destTankId, destinationNodeId);
      }      
      // Handle TREATMENT, ANALYSIS operations
      else if (["TREATMENT", "ANALYSIS"].includes(operation.operation_type)) {
        // return true;
        // Add a parent node for the operation
        const parentNodeId = `parent_${operation.operation_id}`;
        if (operation.treatments?.[0]?.source_batch_color) {
          operation.treatments[0].color = getColor(operation.treatments?.[0]?.source_batch_color);
        }
        addNode(nodes, { // 5: Treatment/Analysis node
          id: parentNodeId,
          // x: xOffsetStart,
          // y: yOffset + 100,
          data: {
            operation_id: operation.operation_id,
            operation_type: operation.operation_type,
            completion_date: operation.completion_date,
            treatments: operation?.treatments || [],
          }
        });

        const firstTransfer = operation?.transfers?.[0];
        if (i === 0) {
          // First operation (TREATMENT or ANALYSIS) as before
          if (data[i + 1]) {
            const nextOperation = data[i + 1];
            // console.log('prevOperation?.treatments', nextOperation?.treatments)

            const nextNodeId = `parent_${nextOperation.operation_id}`;
            addEdge({
              edges,
              id: `e_${parentNodeId}-${nextNodeId}`,
              source: parentNodeId,
              target: nextNodeId,
              color: firstTransfer?.color
            });            
          }
        } else {
          // Not the first operation (TREATMENT or ANALYSIS)
          const prevOperation = data[i - 1];
          
          // Identify the most recent tank node from the previous operation's transfers
          let previousTankNodeId = null;
          const isPreviousWineMovingOp = ['DESTEMMING', 'DECANT', 'RACKING', 'CUT', 'NEW_LOT', 'BOTTLING'].includes(prevOperation?.operation_type);
          if (prevOperation?.transfers?.length > 0 && isPreviousWineMovingOp) {
            const previousTransfer = prevOperation.transfers[prevOperation.transfers.length - 1];
            previousTankNodeId = !isPreviousWineMovingOp ? `${prevOperation.operation_id}_${previousTransfer.destination_tank_id}` : `${prevOperation.operation_id}_${id}`;
            // console.log('prevOperation?.treatments *****************7', operation?.treatments?.[0]?.tank_id === Number(id))

          } else if (prevOperation?.treatments?.length > 0 || prevOperation?.winemakings?.length > 0 ) {
            previousTankNodeId = `parent_${prevOperation.operation_id}`;
            // TO DO, it works with 2 consecutives ANALYSIS, test with Automatic operations
          }
    
          if (previousTankNodeId) {
            // Connect from the tank node to the current operation node
            addEdge({
              edges,
              id: `e_${previousTankNodeId}-${parentNodeId}`,
              source: previousTankNodeId,
              target: parentNodeId,
              color: firstTransfer?.color || operation?.treatments?.[0]?.color || operation?.winemakings?.[0]?.color,
              isFocused: flowType === 'tank' && (operation?.treatments?.[0]?.tank_id === Number(id) || operation?.winemakings?.[0]?.tank_id === Number(id)),
            });            
          }
        }
      }
    });
    
    const uniqueNodes = Array.from(new Map(nodes.map(n => [n.id, n])).values());
    const uniqueEdges = Array.from(new Map(edges.map(e => [e.id, e])).values());
    
    return { nodes: uniqueNodes, edges: uniqueEdges };
    // return { nodes, edges };
  };

  // Handler for node clicks
  const handleNodeClick = (event, node) => {
    const operation_id = node?.data?.operation_id;
    if (operation_id) {
      toggleShowOperationData(operation_id);
      // fitView({ padding: 0.1, duration: 800 }); // Fit view with animation
    }
  };

  const { nodes, edges } = initialNodesAndEdges(operations);
  const nodesWithLayout = applyDagreLayout(nodes, edges);

  return (
      <div style={{ height: "65vh" }}>
        <ReactFlow
          nodes={nodesWithLayout}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={handleNodeClick}
          fitView
        >
          <MiniMap nodeColor={() => "#8b0000"} />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
  );
};

export default FlowChart;
